import { Controller } from 'stimulus'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import './shared/map_experience.css'

import { getRequest } from '@react/helpers/fetch'
import { csrfToken }  from '@react/helpers/fetch'
import { createBaseMap, destroyBaseMap, markerIcon, dotIcon, stopIcon, vanIcon, hasUsableSize } from './shared/leaflet_map'
import MapLegend, { markIcon, dotStyle } from './shared/map_legend'
import { labelPinIcon } from './shared/coordination_pins'

const POPUP_OPTIONS = { maxWidth: 420, minWidth: 240, autoPanPadding: [30, 30] }
// Up to this many places in view, each one is a pin; past it the view is drawn as dots.
const PINS_IN_VIEW = 250
const FOLLOW_KEY   = 'map.follow'
const TONE_KEY     = 'map.tone'
const DEFAULT_TONE = 30
// Dark whatever the technician's colour: it has to read over any plan.
const TOUR_LINE = '#1e3a8a'
const GPS_LINE  = '#fc4c02'
const DOT     = { radius: 5, weight: 1.5, color: '#fff', fillColor: '#2563eb', fillOpacity: 0.95 }
const HOT_DOT = { radius: 8, fillColor: '#1d4ed8' }

export default class extends Controller {
  static targets = ['controls', 'container', 'count']

  connect() {
    this.markers = JSON.parse(this.data.get('markers'))
    this.routes  = JSON.parse(this.data.get('routes') || '[]')
    this.tour    = JSON.parse(this.data.get('tour') || 'null')
    if (this.data.has('vehicle-markers')) {
      this.vehicleMarkers = JSON.parse(this.data.get('vehicle-markers'))
      this.allMarkers = this.markers.concat(this.vehicleMarkers)
    } else {
      this.allMarkers = this.markers
    }
    this.allMarkers = this.allMarkers.filter(marker => marker.lat != null && marker.lng != null)
    this._cap    = parseInt(this.data.get('cap'), 10) || Infinity
    this._pins   = parseInt(this.data.get('pins-in-view'), 10) || PINS_IN_VIEW
    this._follow = this._readFollow()
    // Pins follow the view when the page holds only a part of them: a bounded page, or a capped one.
    this._viewFollows = this._urlBounds() != null || this.allMarkers.length >= this._cap
    this.layers  = new Map()
    document.addEventListener('workorder:updated', this.recordUpdated)
    this._whenSized(() => {
      this._renderMap()
      this._renderToolbar()
      this._renderLegend()
      this._bindList()
      this._resetZoom()
      this._renderViewport()
      this._drawRoutes()
      this._drawTour(this.tour)
    })
  }

  _whenSized(callback, tries = 0) {
    const element = this.containerTarget
    if (element && element.offsetWidth > 50 && element.offsetHeight > 50) return callback()
    if (tries > 60) return callback()

    requestAnimationFrame(() => this._whenSized(callback, tries + 1))
  }

  disconnect() {
    document.removeEventListener('workorder:updated', this.recordUpdated)
    this._stopTour()
    clearTimeout(this._moveTimer)
    clearTimeout(this._settleTimer)
    destroyBaseMap(this.map)
    this.map = null
  }

  clickIcon = e => {
    const record = e.currentTarget.dataset.record
    this._showMarker(record)
  }

  clickCard = e => {
    const card   = e.currentTarget.closest(".small_cards__item")
    const record = card.dataset.record

    document.querySelectorAll('.selected-card').forEach(smallCard =>
      smallCard.classList.remove('selected-card')
    )
    card.classList.add('selected-card')

    this._showMarker(record)
  }

  moved() {
    this._renderViewport()
    if (!this.data.has("refresh")) return
    if (this._settling) return

    clearTimeout(this._moveTimer)
    if (this._follow) {
      this._moveTimer = setTimeout(() => this.search({ quiet: true }), 450)
    } else {
      this._areaButton.classList.add('is-visible')
    }
  }

  searchArea = () => {
    this._areaButton.classList.remove('is-visible')
    this.search({ quiet: true })
  }

  toggleFollow = e => {
    this._follow = e.target.checked
    try { localStorage.setItem(FOLLOW_KEY, this._follow ? '1' : '0') } catch (_) {}
    if (this._follow) this.searchArea()
  }

  recordUpdated = e => {
    if (!this.map || !this.data.has('refresh')) return
    this.search({ quiet: true, markers: true }).then(() => {
      const marker = this.allMarkers.find(item => String(item.details) === String(e.detail.id))
      if (!marker) return
      const latlng = [marker.lat, marker.lng]
      if (!this.map.getBounds().contains(latlng)) this._settle(() => this.map.panTo(latlng))
      this._hot(this._key(marker), true)
      setTimeout(() => this._hot(this._key(marker), false), 1500)
    })
  }

  search({ quiet = false, markers = false, view = true } = {}) {
    const url = new URL(window.location.href)
    if (!quiet) {
      this.controlsTarget.innerHTML = `
        <div class="loading-spinner-container">
          <div id="loading-spinner"></div>
        </div>
      `
    }
    if (view) {
      url.searchParams.set("bounds", this._boundsParam())
      url.searchParams.set("center", this._centerParam())
    }
    if (this._legendFiltersList()) {
      const hidden = [...this._legend.hidden.groups]
      if (hidden.length) url.searchParams.set("hidden_types", hidden.join(","))
      else url.searchParams.delete("hidden_types")
    }
    history.replaceState(history.state, document.title, url.href)
    const path = url.pathname + ".json" + url.search
    const request = this._request = getRequest(path)
    return request.then(data => {
      if (request !== this._request) return
      this.controlsTarget.innerHTML = data.html
      if (this.hasCountTarget) {
        this.countTarget.innerHTML = data.count
      }
      reloadReact(this.controlsTarget)
      if (this._legend) this._legend.applyToCards(this.controlsTarget)
      const authenticityToken = this.controlsTarget.querySelector("input[name='authenticity_token']")
      if (authenticityToken) {
        authenticityToken.value = csrfToken()
      }
      // Every place is already on the map: a move only follows the list.
      if (quiet && !markers && !this._viewFollows) return
      this._viewFollows = this._viewFollows || !quiet
      this.allMarkers = data.markers
      if (this._legend) this._legend.update(this.allMarkers)
      if (data.tour !== undefined && this._tourSignature(data.tour) !== this._tourSignature(this.tour)) {
        this.tour = data.tour
        this._drawTour(this.tour)
      }
      this._renderViewport()
    })
  }

  _tourSignature(tour) {
    if (!tour) return ''
    return JSON.stringify([tour.distance, tour.stops.map(stop => [stop.id, stop.state, stop.at, stop.lat, stop.lng])])
  }

  _withPopup(marker, layer) {
    if (marker.infoWindow && marker.infoWindow.content) {
      layer.bindPopup(marker.infoWindow.content, POPUP_OPTIONS)
      return Promise.resolve(layer)
    }
    const url = this._popupUrl(marker)
    if (!url) return Promise.resolve(layer)
    if (layer.getPopup()) return Promise.resolve(layer)

    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then(response => (response.ok ? response.text() : ''))
      .then(html => {
        if (html) layer.bindPopup(html, POPUP_OPTIONS)
        return layer
      })
      .catch(() => layer)
  }

  _popupUrl(marker) {
    if (marker.popup) return marker.popup
    const template = this.data.get('popup')
    return template && marker.details != null ? template.replace(':id', marker.details) : null
  }

  _urlBounds() {
    const param = new URL(window.location.href).searchParams.get('bounds')
    if (!param) return null
    const [south, west, north, east] = param.split(',').map(Number)
    return [south, west, north, east].some(isNaN) ? null : [[south, west], [north, east]]
  }

  _boundsParam() {
    const bounds = this.map.getBounds()
    return [bounds.getSouth(), bounds.getWest(), bounds.getNorth(), bounds.getEast()].join(",")
  }

  _centerParam() {
    const center = this.map.getCenter()
    return [center.lat, center.lng].join(",")
  }

  _key(marker) {
    return String(marker.details ?? marker.popup ?? `${marker.lat},${marker.lng}`)
  }

  _showMarker(record) {
    const key = String(record)
    this.map.closePopup()
    if (this._tourStops && this._tourStops.has(key) && !this._revealed.has(key)) {
      this._revealed.add(key)
      this._renderViewport()
    }
    if (!this.layers.has(key)) {
      const marker = this.allMarkers.find(item => this._key(item) === key)
      if (!marker) return
      this._settle(() => this.map.setView([marker.lat, marker.lng], Math.max(this.map.getZoom(), 15)))
    }
    const entry = this.layers.get(key)
    if (!entry) return

    this._withPopup(entry.marker, entry.layer).then(layer => this._settle(() => layer.openPopup()))
  }

  // Moves the page makes itself (fit, zoom to a marker) are not searches.
  _settle(callback, delay = 600) {
    this._settling = true
    callback()
    clearTimeout(this._settleTimer)
    this._settleTimer = setTimeout(() => { this._settling = false }, delay)
  }

  // Only what is in view is on the map; layers already there stay, so nothing blinks.
  _renderViewport() {
    if (!this.map) return
    const bounds  = this.map.getBounds().pad(0.2)
    const visible = this.allMarkers.filter(marker => {
      if (!bounds.contains([marker.lat, marker.lng])) return false
      if (this._legend && !this._legend.visible(marker)) return false
      const key = this._key(marker)
      return !this._tourStops || !this._tourStops.has(key) || this._revealed.has(key)
    })
    const mode    = visible.length > this._pins && this.routes.length === 0 ? 'dots' : 'pins'

    if (mode !== this._mode) {
      this.markerLayer.clearLayers()
      this.layers.clear()
      this._mode = mode
    }

    const wanted = new Set()
    visible.forEach(marker => {
      const key   = this._key(marker)
      const entry = this.layers.get(key)
      wanted.add(key)
      if (entry && (entry.marker.lat !== marker.lat || entry.marker.lng !== marker.lng || entry.marker.icon !== marker.icon || entry.marker.color !== marker.color || entry.marker.ring !== marker.ring || entry.marker.hollow !== marker.hollow || entry.marker.pin !== marker.pin || entry.marker.label !== marker.label)) {
        this.markerLayer.removeLayer(entry.layer)
        this.layers.delete(key)
      }
      if (!this.layers.has(key)) this.layers.set(key, this._addLayer(key, marker, mode))
    })
    this.layers.forEach((entry, key) => {
      if (wanted.has(key) || entry.layer.isPopupOpen()) return
      this.markerLayer.removeLayer(entry.layer)
      this.layers.delete(key)
    })
  }

  _addLayer(key, marker, mode) {
    const latlng = [marker.lat, marker.lng]
    const stop   = this._tourStops && this._tourStops.get(key)
    // Out-of-order units stay HTML pins in the dense mode: a canvas dot cannot pulse.
    const layer  = mode === 'dots' && !marker.top && !marker.ring
      ? L.circleMarker(latlng, { ...dotStyle(marker, DOT), renderer: this.canvas })
      : L.marker(latlng, { icon: stop ? stopIcon(stop) : marker.pin ? labelPinIcon(marker.pin, marker.label) : marker.color && !marker.icon ? this._dotIcon(marker) : markerIcon(marker.icon), riseOnHover: true })
    if (marker.tooltip) layer.bindTooltip(marker.tooltip, { direction: 'top', offset: [0, -30] })
    if (!stop && this.tour && key === 'base-location') {
      layer.bindTooltip(`${this.data.get('tour-start')} · ${this.data.get('tour-return')}`, { permanent: true, direction: 'right', offset: [12, -18], className: 'tour-label' })
    }

    if (marker.infoWindow && marker.infoWindow.content) {
      layer.bindPopup(marker.infoWindow.content, POPUP_OPTIONS)
    } else if (this._popupUrl(marker)) {
      layer.on('click', () => this._withPopup(marker, layer).then(bound => bound.openPopup()))
    }

    if (marker.card) {
      layer.on('click', () => {
        const card = document.getElementById(marker.card)
        if (!card) return
        unselectCards()
        card.querySelector(".small_cards__item").classList.add('selected-card')
        card.scrollIntoView({ block: "center", behavior: "smooth" })
      })
    }

    layer.on('mouseover', () => this._hot(key, true))
    layer.on('mouseout',  () => this._hot(key, false))
    this.markerLayer.addLayer(layer)

    return { layer, marker }
  }

  _hot(key, on) {
    const entry = this.layers.get(key)
    if (entry) {
      if (entry.layer.setStyle) {
        entry.layer.setStyle(on ? HOT_DOT : dotStyle(entry.marker, DOT))
        if (on) entry.layer.bringToFront()
      } else {
        const element = entry.layer.getElement()
        if (element) element.classList.toggle('is-hot', on)
      }
    }
    const card = this._card(this.controlsTarget.querySelector(`[data-record="${key}"]`))
    if (card) card.classList.toggle('is-hot', on)
  }

  // The record sits on the card itself or on its pin icon, depending on the map.
  _card(element) {
    const container = this._cardContainer(element)
    if (!container) return null
    return container.querySelector('.small_cards__item') || container
  }

  _cardContainer(element) {
    if (!element || !element.closest) return null
    return element.closest('.map__small-card__container') || element.closest('.small_cards__item')
  }

  _cardRecord(element) {
    const container = this._cardContainer(element)
    if (!container) return null
    const holder = container.matches('[data-record]') ? container : container.querySelector('[data-record]')
    return holder ? String(holder.dataset.record) : null
  }

  _bindList() {
    this.controlsTarget.addEventListener('mouseover', e => {
      const record = this._cardRecord(e.target)
      if (record) this._hot(record, true)
    })
    this.controlsTarget.addEventListener('mouseout', e => {
      const record = this._cardRecord(e.target)
      if (record && this._cardRecord(e.relatedTarget) !== record) this._hot(record, false)
    })
  }

  _dotIcon(marker) {
    return marker.ring || marker.hollow || marker.top ? markIcon(marker) : dotIcon(marker.color)
  }

  _renderLegend() {
    if (!this.data.has('legend')) return
    this._legend = new MapLegend(this.map, JSON.parse(this.data.get('legend')), {
      onChange: () => {
        this._renderViewport()
        this._legend.applyToCards(this.controlsTarget)
        if (this._legendFiltersList()) this.search({ quiet: true, view: false })
      }
    })
    this._legend.update(this.allMarkers)
    this._legend.applyToCards(this.controlsTarget)
    if (this._legendFiltersList() && this._legend.hidden.groups.size) this.search({ quiet: true, view: false })
  }

  _legendFiltersList() {
    return !!(this._legend && this._legend.legend.list && this.data.has('refresh'))
  }

  _readFollow() {
    try { return localStorage.getItem(FOLLOW_KEY) !== '0' } catch (_) { return true }
  }

  _renderToolbar() {
    if (!this.data.has('refresh')) return

    const bar = L.DomUtil.create('div', 'map-toolbar', this.containerTarget)
    bar.innerHTML = `
      <label class="map-toolbar__follow">
        <input type="checkbox" ${this._follow ? 'checked' : ''}>
        <span>${this.data.get('follow-label')}</span>
      </label>
      <button type="button" class="map-toolbar__area">${this.data.get('area-label')}</button>
    `
    L.DomEvent.disableClickPropagation(bar)
    L.DomEvent.disableScrollPropagation(bar)
    bar.querySelector('input').addEventListener('change', this.toggleFollow)
    this._areaButton = bar.querySelector('.map-toolbar__area')
    this._areaButton.addEventListener('click', this.searchArea)
  }

  _renderTone() {
    let tone = DEFAULT_TONE
    try { tone = parseInt(localStorage.getItem(TONE_KEY), 10) } catch (_) {}
    if (isNaN(tone)) tone = DEFAULT_TONE
    const apply = value => this.containerTarget.style.setProperty('--map-saturation', value / 100)
    apply(tone)

    const control = L.control({ position: 'topright' })
    control.onAdd = () => {
      const box = L.DomUtil.create('div', 'map-tone leaflet-bar')
      box.innerHTML = `
        <label title="${this.data.get('tone-label')}">
          <span class="map-tone__swatch map-tone__swatch--full"></span>
          <input type="range" min="0" max="100" value="${tone}" aria-label="${this.data.get('tone-label')}">
          <span class="map-tone__swatch"></span>
        </label>
      `
      L.DomEvent.disableClickPropagation(box)
      L.DomEvent.disableScrollPropagation(box)
      const input = box.querySelector('input')
      input.addEventListener('input',  () => apply(input.value))
      input.addEventListener('change', () => { try { localStorage.setItem(TONE_KEY, input.value) } catch (_) {} })
      return box
    }
    control.addTo(this.map)
  }

  _drawTour(tour) {
    this._stopTour()
    this._tourStops = new Map()
    this._revealed  = new Set()
    if (!tour || !tour.legs.length) return

    tour.stops.forEach(stop => this._tourStops.set(String(stop.id), stop))
    this.layers.forEach(entry => { this.markerLayer.removeLayer(entry.layer) })
    this.layers.clear()
    this._renderViewport()

    // The line, and where along it each stop is reached: its pin appears as the line gets there.
    const path    = []
    const reachAt = new Map()
    tour.legs.forEach((leg, i) => {
      if (i === 0) {
        const first = tour.stops.find(stop => stop.index === leg.from)
        if (first) reachAt.set(String(first.id), 0)
      }
      path.push(...(i ? leg.coordinates.slice(1) : leg.coordinates))
      const stop = tour.stops.find(stop => stop.index === leg.to)
      if (stop) reachAt.set(String(stop.id), path.length - 1)
    })

    this.tourLayer = L.layerGroup().addTo(this.map)
    if (tour.track && tour.track.length > 1) {
      L.polyline(tour.track.map(point => [point.lat, point.lng]), { color: GPS_LINE, weight: 3, opacity: .8, dashArray: '2 6', lineCap: 'round', interactive: false })
        .bindTooltip(this.data.get('tour-track'), { sticky: true, className: 'tour-label' }).addTo(this.tourLayer)
    }
    const ink = L.polyline([], { color: TOUR_LINE, weight: 5, opacity: .95, lineCap: 'round', lineJoin: 'round', interactive: false }).addTo(this.tourLayer)
    this._ink = ink
    this._tourPath = path
    this._tourReach = reachAt
    const total = path.length
    const started = performance.now()
    const reveal = now => {
      const t = Math.min((now - started) / 10000, 1)
      const eased = t < .5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2
      const shown = Math.max(2, Math.round(eased * total))
      ink.setLatLngs(path.slice(0, shown))
      let reached = false
      reachAt.forEach((at, key) => {
        if (this._revealed.has(key) || shown - 1 < at) return
        this._revealed.add(key)
        reached = true
      })
      if (reached) this._renderViewport()
      if (t < 1) { this._tourFrame = requestAnimationFrame(reveal); return }
      this._tourStops.forEach((_stop, key) => this._revealed.add(key))
      this._renderViewport()
      this._placeVan(tour, path, true)
      this._tourTimer = setInterval(() => this._placeVan(tour, path, false), 30000)
    }
    this._tourFrame = requestAnimationFrame(reveal)
    this._drawSummary(tour)
  }

  _stopTour() {
    cancelAnimationFrame(this._tourFrame)
    cancelAnimationFrame(this._flyFrameId)
    clearInterval(this._tourTimer)
    if (this._fly) { this._flyRelease(); this._fly = null }
    if (this.tourLayer) { this.map.removeLayer(this.tourLayer); this.tourLayer = null }
    if (this._summary) { this._summary.remove(); this._summary = null }
    this._van = null
  }

  // Times come from the plan; the road time from the routed day, or from the router's total.
  _tourProgress(tour) {
    const now   = Date.now()
    const legs  = tour.legs
    const stops = tour.stops
    const share = tour.duration ? tour.duration / legs.length : 1800
    const first = stops[0]
    const last  = stops[stops.length - 1]
    if (!first.at) return { leg: legs.length - 1, t: 1 }

    const times = legs.map((leg, i) => {
      const toStop   = stops.find(stop => stop.index === leg.to)
      const fromStop = stops.find(stop => stop.index === leg.from)
      const road     = (toStop ? toStop.travel : last.back) || leg.seconds || share
      const arrival  = toStop ? Date.parse(toStop.at) : Date.parse(last.until || last.at) + road * 1000
      const leave    = fromStop ? Math.min(Date.parse(fromStop.until || fromStop.at), arrival - road * 1000) : arrival - road * 1000
      return { leave, arrival, toStop }
    })

    for (let i = 0; i < times.length; i++) {
      const { leave, arrival, toStop } = times[i]
      if (now < leave) return { leg: i, t: 0 }
      if (now < arrival) return { leg: i, t: (now - leave) / Math.max(arrival - leave, 1) }
      if (toStop && toStop.status !== 'done' && now < Date.parse(toStop.until || toStop.at)) return { leg: i, t: 1 }
    }
    return { leg: legs.length - 1, t: 1 }
  }

  _pointAt(tour, path, progress) {
    const leg = tour.legs[progress.leg]
    const coordinates = leg.coordinates
    if (progress.t <= 0) return coordinates[0]
    if (progress.t >= 1) return coordinates[coordinates.length - 1]
    const lengths = []
    let total = 0
    for (let i = 1; i < coordinates.length; i++) {
      total += this.map.distance(coordinates[i - 1], coordinates[i])
      lengths.push(total)
    }
    const target = total * progress.t
    const i = lengths.findIndex(length => length >= target)
    if (i < 0) return coordinates[coordinates.length - 1]
    const before = i === 0 ? 0 : lengths[i - 1]
    const ratio  = (target - before) / Math.max(lengths[i] - before, 1e-9)
    const [a, b] = [coordinates[i], coordinates[i + 1]]
    return [a[0] + (b[0] - a[0]) * ratio, a[1] + (b[1] - a[1]) * ratio]
  }

  _placeVan(tour, path, ride) {
    const progress = this._tourProgress(tour)
    const target   = this._pointAt(tour, path, progress)
    if (!this._van) {
      this._van = L.marker(path[0], { icon: vanIcon(TOUR_LINE), zIndexOffset: 2000, interactive: false }).addTo(this.tourLayer)
    }
    if (!ride) { this._glide(this._van, target, 900); return }

    const index = path.findIndex(point => point[0] === target[0] && point[1] === target[1])
    const route = index >= 0 ? path.slice(0, index + 1) : this._pathUntil(tour, path, progress).concat([target])
    const started = performance.now()
    const duration = Math.min(1500 + route.length * 20, 6000)
    const step = now => {
      const t = Math.min((now - started) / duration, 1)
      const eased = t < .5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2
      const at = Math.min(Math.floor(eased * (route.length - 1)), route.length - 1)
      this._van.setLatLng(route[at])
      if (t < 1) this._tourFrame = requestAnimationFrame(step)
      else this._van.setLatLng(target)
    }
    this._tourFrame = requestAnimationFrame(step)
  }

  _pathUntil(tour, path, progress) {
    return tour.legs.slice(0, progress.leg).flatMap((leg, i) => (i ? leg.coordinates.slice(1) : leg.coordinates))
      .concat(progress.t > 0 ? tour.legs[progress.leg].coordinates.slice(0, -1) : [])
  }

  _glide(marker, target, duration) {
    const from = marker.getLatLng()
    const started = performance.now()
    const step = now => {
      const t = Math.min((now - started) / duration, 1)
      marker.setLatLng([from.lat + (target[0] - from.lat) * t, from.lng + (target[1] - from.lng) * t])
      if (t < 1) requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  }

  _drawSummary(tour) {
    const done = tour.stops.filter(stop => stop.status === 'done').length
    const km   = tour.distance ? Math.round(tour.distance / 1000) : '–'
    const text = this.data.get('tour-summary').replace('%{stops}', tour.stops.length).replace('%{done}', done).replace('%{km}', km)
    const control = L.control({ position: 'bottomright' })
    control.onAdd = () => {
      const box = L.DomUtil.create('div', 'tour-panel')
      box.innerHTML = `
        <div class="tour-player">
          <button type="button" class="tour-player__play" title="${this.data.get('tour-play')}"><i class="fas fa-play"></i></button>
          <button type="button" class="tour-player__speed" title="${this.data.get('tour-speed')}">1×</button>
          <input type="range" class="tour-player__bar" min="0" max="1000" value="0" aria-label="${this.data.get('tour-play')}">
          <button type="button" class="tour-player__3d" title="${this.data.get('tour-fly3d')}">3D</button>
        </div>
        <div class="tour-summary">
          <span class="tour-summary__dot" style="background:${tour.color}"></span><strong>${tour.name}</strong><span>${text}</span>
        </div>`
      L.DomEvent.disableClickPropagation(box)
      L.DomEvent.disableScrollPropagation(box)
      this._flyButton = box.querySelector('.tour-player__play')
      this._flySpeed  = box.querySelector('.tour-player__speed')
      this._flyBar    = box.querySelector('.tour-player__bar')
      this._flyButton.addEventListener('click', () => this._flyToggle(tour))
      this._flySpeed.addEventListener('click', () => this._flyFaster())
      this._flyBar.addEventListener('input', () => this._flySeek(tour))
      box.querySelector('.tour-player__3d').addEventListener('click', () => this._fly3d(tour))
      return box
    }
    control.addTo(this.map)
    this._summary = control
  }

  // The 3D engine loads only when asked for.
  _fly3d(tour) {
    if (this._fly && this._fly.playing) this._flyPause()
    const labels = ['play', 'pause', 'speed', 'close', 'track'].reduce((all, key) => ({ ...all, [key]: this.data.get(`tour-${key}`) }), {})
    import('./shared/tour_flyover_3d').then(module => module.openFlyover3d({ tour, labels, speed: this._flySpeedChoice || 1 }))
  }

  _flyToggle(tour) {
    if (!this._fly) this._flyStart(tour)
    else if (this._fly.playing) this._flyPause()
    else this._flyResume()
  }

  _flyStart(tour) {
    const path     = this._tourPath
    const legStart = []
    let index = 0
    tour.legs.forEach((leg, i) => {
      legStart.push(i ? index - 1 : 0)
      index += i ? leg.coordinates.length - 1 : leg.coordinates.length
    })
    const lengths  = tour.legs.map(leg => this._legMetrics(leg).total)
    const longest  = Math.max(...lengths, 1)
    const timeline = []
    let at = 0
    tour.legs.forEach((leg, i) => {
      const duration = 2500 + 6500 * (lengths[i] / longest)
      timeline.push({ kind: 'leg', i, start: at, end: at + duration, zoom: this._flyZoomFor(lengths[i]) })
      at += duration
      const stop = tour.stops.find(item => item.index === leg.to)
      if (stop) { timeline.push({ kind: 'stop', i, stop, start: at, end: at + 1600 }); at += 1600 }
    })

    cancelAnimationFrame(this._tourFrame)
    clearInterval(this._tourTimer)
    this._revealed.clear()
    this._renderViewport()
    this._ink.setLatLngs([])
    if (!this._van) this._van = L.marker(path[0], { icon: vanIcon(TOUR_LINE), zIndexOffset: 2000, interactive: false }).addTo(this.tourLayer)
    this._van.setLatLng(path[0])
    this.map.dragging.disable()
    this.map.scrollWheelZoom.disable()
    this.map.doubleClickZoom.disable()

    this._fly = { tour, path, legStart, timeline, total: at, t: 0, speed: this._flySpeedChoice || 1, playing: true, last: null, zoom: timeline[0].zoom }
    this._flyButton.innerHTML = '<i class="fas fa-pause"></i>'
    this._flyButton.title = this.data.get('tour-pause')
    this._settle(() => this.map.setView(path[0], timeline[0].zoom, { animate: true, duration: .8 }), 1200)
    setTimeout(() => { if (this._fly && this._fly.playing) this._flyFrameId = requestAnimationFrame(this._flyFrame) }, 850)
  }

  _flyPause() {
    this._fly.playing = false
    cancelAnimationFrame(this._flyFrameId)
    this._flyButton.innerHTML = '<i class="fas fa-play"></i>'
    this._flyButton.title = this.data.get('tour-play')
  }

  _flyResume() {
    if (this._fly.t >= this._fly.total) this._fly.t = 0
    this._fly.playing = true
    this._fly.last = null
    this._flyButton.innerHTML = '<i class="fas fa-pause"></i>'
    this._flyButton.title = this.data.get('tour-pause')
    this._flyFrameId = requestAnimationFrame(this._flyFrame)
  }

  _flyFaster() {
    const next   = { 1: 2, 2: 4, 4: 1 }
    const chosen = next[this._flySpeedChoice || 1] || 1
    this._flySpeedChoice = chosen
    if (this._fly) this._fly.speed = chosen
    this._flySpeed.textContent = `${chosen}×`
  }

  _flySeek(tour) {
    if (!this._fly) { this._flyStart(tour); this._flyPause() }
    this._fly.t = this._flyBar.value / 1000 * this._fly.total
    this._fly.last = null
    this._flyApply()
  }

  _flyFrame = now => {
    const fly = this._fly
    if (!fly || !fly.playing) return
    if (fly.last != null) fly.t += (now - fly.last) * fly.speed
    fly.last = now
    if (fly.t >= fly.total) { fly.t = fly.total; this._flyApply(); this._flyEnd(); return }
    this._flyApply()
    this._flyFrameId = requestAnimationFrame(this._flyFrame)
  }

  _flyApply() {
    const fly = this._fly
    const segment = fly.timeline.find(item => fly.t < item.end) || fly.timeline[fly.timeline.length - 1]
    const leg = fly.tour.legs[segment.i]
    let point, index
    if (segment.kind === 'leg') {
      ({ point, index } = this._along(leg, (fly.t - segment.start) / (segment.end - segment.start)))
      fly.zoom = segment.zoom
    } else {
      index = leg.coordinates.length - 1
      point = leg.coordinates[index]
    }
    const pathIndex = fly.legStart[segment.i] + index
    this._ink.setLatLngs(fly.path.slice(0, pathIndex + 1).concat([point]))

    let reached = false
    this._tourReach.forEach((at, key) => {
      if (this._revealed.has(key) || pathIndex < at) return
      this._revealed.add(key)
      reached = true
    })
    if (reached) this._renderViewport()

    if (segment.kind === 'stop' && this._flyLabel !== segment.stop.id) {
      this._flyLabel = segment.stop.id
      const entry = this.layers.get(String(segment.stop.id))
      if (entry) {
        const time = segment.stop.at ? new Date(segment.stop.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''
        entry.layer.bindTooltip(`${segment.stop.index} · ${time}`.trim(), { permanent: true, direction: 'top', offset: [0, -34], className: 'tour-label' }).openTooltip()
        setTimeout(() => { if (entry.layer.unbindTooltip) entry.layer.unbindTooltip() }, 2600 / fly.speed)
      }
    }

    if (this._van) this._van.setLatLng(point)
    this._settle(() => this.map.setView(point, fly.zoom, { animate: false }), 1200)
    this._flyBar.value = Math.round(fly.t / fly.total * 1000)
  }

  _flyEnd() {
    const { tour, path } = this._fly
    this._flyRelease()
    this._fly = null
    this._flyButton.innerHTML = '<i class="fas fa-redo"></i>'
    this._flyButton.title = this.data.get('tour-play')
    this._flyBar.value = 1000
    this._tourStops.forEach((_stop, key) => this._revealed.add(key))
    this._renderViewport()
    this._ink.setLatLngs(path)
    const points = this._bulk(path.filter((_p, i) => i % Math.max(1, Math.floor(path.length / 400)) === 0))
    this._settle(() => this.map.fitBounds(points, { padding: [40, 40], maxZoom: 15 }), 1500)
    setTimeout(() => { if (!this._fly) this._placeVan(tour, path, false) }, 900)
    this._tourTimer = setInterval(() => this._placeVan(tour, path, false), 30000)
  }

  _flyRelease() {
    cancelAnimationFrame(this._flyFrameId)
    this.map.dragging.enable()
    this.map.scrollWheelZoom.enable()
    this.map.doubleClickZoom.enable()
    this._flyLabel = null
  }

  _flyZoomFor(metres) {
    if (metres < 3000)   return 15
    if (metres < 10000)  return 14
    if (metres < 40000)  return 12
    if (metres < 150000) return 10
    return 8
  }

  _along(leg, fraction) {
    const { cums, total } = this._legMetrics(leg)
    const coordinates = leg.coordinates
    if (coordinates.length < 2 || fraction <= 0) return { point: coordinates[0], index: 0 }
    if (fraction >= 1) return { point: coordinates[coordinates.length - 1], index: coordinates.length - 1 }
    const target = total * fraction
    let i = cums.findIndex(cum => cum >= target)
    if (i < 0) i = cums.length - 1
    const before = i === 0 ? 0 : cums[i - 1]
    const ratio  = (target - before) / Math.max(cums[i] - before, 1e-9)
    const [a, b] = [coordinates[i], coordinates[i + 1]]
    return { point: [a[0] + (b[0] - a[0]) * ratio, a[1] + (b[1] - a[1]) * ratio], index: i }
  }

  _legMetrics(leg) {
    if (!this._legCache) this._legCache = new WeakMap()
    let metrics = this._legCache.get(leg)
    if (metrics) return metrics
    const cums = []
    let total = 0
    for (let i = 1; i < leg.coordinates.length; i++) {
      total += this.map.distance(leg.coordinates[i - 1], leg.coordinates[i])
      cums.push(total)
    }
    metrics = { cums, total }
    this._legCache.set(leg, metrics)
    return metrics
  }

  // A page opened with bounds comes back where it was; otherwise the map frames its pins.
  _resetZoom () {
    if (this._fitted) return
    if (!hasUsableSize(this.map)) return
    const urlBounds = this._urlBounds()
    if (urlBounds) {
      this._settle(() => this.map.fitBounds(urlBounds, { animate: false }))
      this._fitted = true
      return
    }
    if (this.allMarkers.length === 0) return

    const points = this._bulk(this.allMarkers.map(marker => [marker.lat, marker.lng]))

    this._settle(() => {
      if (points.length === 1) {
        this.map.setView(points[0], 14)
      } else {
        this.map.fitBounds(points, { padding: [40, 40], maxZoom: 15 })
      }
    })
    this._fitted = true
  }

  // The frame holds the bulk of the pins: a few far away would otherwise shrink the rest to a dot.
  _bulk(points) {
    if (points.length < 10) return points
    const median = axis => {
      const sorted = points.map(point => point[axis]).sort((a, b) => a - b)
      return sorted[Math.floor(sorted.length / 2)]
    }
    const center   = [median(0), median(1)]
    const distance = point => Math.hypot(point[0] - center[0], (point[1] - center[1]) * Math.cos(center[0] * Math.PI / 180))
    const sorted   = points.slice().sort((a, b) => distance(a) - distance(b))
    const kept     = sorted.slice(0, Math.ceil(points.length * 0.8))
    const reach    = distance(kept[kept.length - 1])
    return sorted.filter(point => distance(point) <= Math.max(reach * 3, 0.05))
  }

  _refitWhenSized() {
    if (this._fitted) return

    this._resetZoom()
  }

  _drawRoutes () {
    if (!this.routes || this.routes.length === 0) return

    this.routes.forEach(route => {
      const coordinates = route.coordinates || []
      if (coordinates.length < 2) return

      L.polyline(coordinates, {
        color:     route.strokeColor,
        opacity:   route.strokeOpacity || 0.7,
        weight:    5,
        dashArray: route.dashed ? '6 8' : null
      }).addTo(this.routeLayer)
    })
  }

  _renderMap() {
    this.map = createBaseMap(this.containerTarget, { onResize: () => this._refitWhenSized() })

    this.canvas      = L.canvas({ padding: 0.5 })
    this.routeLayer  = L.layerGroup().addTo(this.map)
    this.markerLayer = L.layerGroup().addTo(this.map)

    this.containerTarget.classList.add('map--muted')
    this.map.on('baselayerchange', e => this.containerTarget.classList.toggle('map--muted', e.name !== 'Satellite'))
    this._renderTone()
    this.map.on('moveend', this.moved.bind(this))
    // A popup opening pans the map too, and that move is not a search.
    this.map.on('autopanstart', () => this._settle(() => {}, 1200))
    this.map.on('popupopen',    () => this._settle(() => {}))
  }
}
